/**
 * smart-cascade.js  v4 – ShirtX Cascade Picker
 * Order: Type → Size → Color  (color is final selection → commits to Shopify)
 * Fixes: correct cascade order, auto color hex, size prefix stripping
 */

(function () {
  'use strict';

  // ── Color hex map (extended) ──────────────────────────────────────────────
  const COLOR_HEX = {
    'Yellow': '#E3C04A', 'Heather Mauve': '#B07A7A', 'Heather Deep Teal': '#3F5B78', 'Kelly': '#4CAF7D', 'Ocean': '#8BB4DE', 'Team Purple': '#3D1A6E', 'Heather Navy': '#3B4466', 'Heather Dark Grey': '#555555', 'Blossom': '#E6C3DA', 'Blue Jean': '#6B7A99', 'Blue Spruce': '#5E7C80', 'Butter': '#F3DC9C', 'Brick': '#6E4448', 'Denim': '#4E5470', 'Gray': '#858382', 'Ivory': '#F2EADF', 'Chambray': '#B8D4E3', 'Espresso': '#6E5E52', 'Moss': '#66735A', 'Light Green': '#6E8B7A', 'Midnight': '#283A5C', 'Orchid': '#B8A6CC', 'Pepper': '#6E6B69', 'Crunchberry': '#B0546C', 'Watermelon': '#DE8584', 'Yam': '#C48C6C',    'White': '#FFFFFF', 'Black': '#1A1A1A', 'Navy': '#1B2A4A', 'Navy Blue': '#1B2A4A',
    'Royal Blue': '#2B5CE6', 'Royal': '#2B5CE6', 'Red': '#D62828', 'True Red': '#CC2200',
    'Forest Green': '#2D6A4F', 'Forest': '#2D6A4F', 'Dark Green': '#1A4A2A',
    'Heather Gray': '#A8A8A8', 'Heather Grey': '#A8A8A8', 'Sport Gray': '#B8B8B8',
    'Sport Grey': '#B8B8B8', 'Dark Heather': '#4A4A4A', 'Dark Heather Gray': '#4A4A4A',
    'Graphite Heather': '#5A5A5A', 'Antique Cherry Red': '#A52020',
    'Light Blue': '#A8D8EA', 'Sky': '#87CEEB', 'Sky Blue': '#87CEEB',
    'Carolina Blue': '#56A0D3', 'Powder Blue': '#B0D4E8', 'Sapphire': '#1A4870',
    'Antique Sapphire': '#1A4870', 'Indigo Blue': '#3A3F8F',
    'Gold': '#D4A017', 'Old Gold': '#C8980A', 'Daisy': '#F5C542',
    'Purple': '#6A0572', 'Violet': '#7F00FF', 'Maroon': '#800000',
    'Antique Maroon': '#6B1A1A', 'Cardinal': '#9B2335', 'Cardinal Red': '#9B2335',
    'Orange': '#D4560A', 'Safety Orange': '#E05000', 'Tennessee Orange': '#E06020',
    'Pink': '#FFAFCC', 'Azalea': '#F06090', 'Heliconia': '#E8457C',
    'Neon Pink': '#FF1493', 'Light Pink': '#FFB6C1',
    'Sand': '#C8A882', 'Natural': '#F0EAD6', 'Ash': '#C8C8C8', 'Ash Grey': '#C8C8C8',
    'Military Green': '#4A5240', 'Olive': '#6B6B2A', 'Camo': '#4A5240',
    'Charcoal': '#3D3D3D', 'Dark Chocolate': '#3D1A0A',
    'Irish Green': '#2A7A3A', 'Kiwi': '#8CC63F', 'Mint Green': '#98FF98',
    'Lime': '#32CD32', 'Safety Green': '#ADFF2F', 'Neon Green': '#39FF14',
    'Brown Savana': '#8B6347', 'Chestnut': '#954535', 'Russet': '#80461B',
    'Ice Blue': '#D0ECF8', 'Glacier': '#A0C8D8', 'Silver': '#C0C0C0',
    'Tweed': '#C4A882', 'Tan': '#D2B48C',
    'Coral': '#FF6B6B', 'Sunset': '#FA7050',
    'Cobalt Blue': '#0047AB', 'Electric Blue': '#1B4FFF',
  };

  function getHex(name) {
    if (COLOR_HEX[name]) return COLOR_HEX[name];
    // Try case-insensitive match
    const lower = name.toLowerCase();
    for (const [k, v] of Object.entries(COLOR_HEX)) {
      if (k.toLowerCase() === lower) return v;
    }
    // Try partial match (e.g. "Heather Blue" → check if any word matches)
    for (const [k, v] of Object.entries(COLOR_HEX)) {
      if (lower.includes(k.toLowerCase()) || k.toLowerCase().includes(lower)) return v;
    }
    // Use browser CSS color parsing as last resort
    try {
      const c = document.createElement('canvas'); c.width = c.height = 1;
      const ctx = c.getContext('2d');
      ctx.fillStyle = name; ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      // If not default black (0,0,0), use it
      if (d[0] !== 0 || d[1] !== 0 || d[2] !== 0) return `rgb(${d[0]},${d[1]},${d[2]})`;
      // If it IS black, check if the name actually meant black
      if (lower.includes('black') || lower.includes('dark')) return '#1A1A1A';
    } catch(e) {}
    return '#CCCCCC';
  }

  function isLight(hex) {
    try {
      const c = document.createElement('canvas'); c.width = c.height = 1;
      const ctx = c.getContext('2d');
      ctx.fillStyle = hex; ctx.fillRect(0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      return (0.299 * d[0] + 0.587 * d[1] + 0.114 * d[2]) / 255 > 0.70;
    } catch(e) { return false; }
  }

  const SIZE_ORDER = [
    'NB','0-3M','3-6M','6-12M','12-18M','18-24M',
    '2T','3T','4T','5T',
    'YXS','YS','YM','YL','YXL',
    'XS','S','M','L','XL','2XL','3XL','4XL','5XL',
  ];

  function sortSizes(sizes) {
    return [...sizes].sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a), bi = SIZE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1; if (bi === -1) return -1;
      return ai - bi;
    });
  }

  /**
   * Strip "TypeName - " prefix from size display labels.
   * "Adult Unisex - S" → "S", "Youth - YM" → "YM", "2T" → "2T"
   */
  function stripSizePrefix(sizeValue) {
    const m = sizeValue.match(/^.+?\s+-\s+(.+)$/);
    return m ? m[1] : sizeValue;
  }

  function esc(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── Main init ─────────────────────────────────────────────────────────────

  function initPicker(containerEl) {
    if (containerEl.dataset.cascadeReady) return;
    containerEl.dataset.cascadeReady = '1';

    const sectionId = containerEl.dataset.section;
    const nativeEl  = document.getElementById(containerEl.dataset.nativeId);
    const jsonEl    = document.getElementById('svs-variants-' + sectionId);
    if (!nativeEl || !jsonEl) return;

    let variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch(e) { return; }
    if (!variants || !variants.length) return;

    // ── Detect native option positions ───────────────────────────────────
    const fieldsets = Array.from(nativeEl.querySelectorAll('fieldset'));
    let typeIdx = -1, colorIdx = -1, sizeIdx = -1;

    fieldsets.forEach((fs, i) => {
      const name = (fs.querySelector('legend')?.textContent || '').toLowerCase().trim();
      if (['type','style','garment','shirt type','product type'].includes(name)) typeIdx = i;
      else if (['color','colour','renk','ink color'].includes(name))             colorIdx = i;
      else if (['size','beden','sizes'].includes(name))                           sizeIdx = i;
    });

    if (typeIdx === -1 && fieldsets.length >= 3) { typeIdx=0; colorIdx=1; sizeIdx=2; }
    if (typeIdx === -1 || colorIdx === -1 || sizeIdx === -1) return;

    const typeFS  = fieldsets[typeIdx];
    const colorFS = fieldsets[colorIdx];
    const sizeFS  = fieldsets[sizeIdx];

    const typeLabelText  = typeFS.querySelector('legend')?.textContent?.trim()  || 'Type';
    const colorLabelText = colorFS.querySelector('legend')?.textContent?.trim() || 'Color';
    const sizeLabelText  = sizeFS.querySelector('legend')?.textContent?.trim()  || 'Size';

    // ── Build variant map ─────────────────────────────────────────────────
    // UI order: Type → Size → Color
    // vmap[type][size_raw] = Set of color values
    // idmap[type][size_raw][color] = { id, available }
    const vmap  = {};  // [type][sizeRaw] = Set<color>
    const idmap = {};  // [type][sizeRaw][color] = { id, available }

    variants.forEach(v => {
      const opts  = [v.option1, v.option2, v.option3];
      const type  = opts[typeIdx]  || '';
      const color = opts[colorIdx] || '';
      const sizeR = opts[sizeIdx]  || '';   // raw size value (may have prefix)

      if (!vmap[type])          vmap[type] = {};
      if (!vmap[type][sizeR])   vmap[type][sizeR] = new Set();
      vmap[type][sizeR].add(color);

      if (!idmap[type])               idmap[type] = {};
      if (!idmap[type][sizeR])        idmap[type][sizeR] = {};
      idmap[type][sizeR][color] = { id: v.id, available: v.available };
    });

    // ── State ─────────────────────────────────────────────────────────────
    let selType = null, selSizeRaw = null, selColor = null;

    // ── Build DOM ─────────────────────────────────────────────────────────
    containerEl.innerHTML = '';

    // 1. TYPE group
    const typeGroup = mk('div', 'cp-group');
    typeGroup.appendChild(mkLabel(typeLabelText, 'cp-type-val-' + sectionId));
    const typeRow = mk('div', 'cp-type-row');
    Object.keys(vmap).forEach(type => {
      const btn = mk('button', 'cp-type-btn');
      btn.type = 'button'; btn.textContent = type; btn.dataset.value = type;
      typeRow.appendChild(btn);
    });
    typeGroup.appendChild(typeRow);
    containerEl.appendChild(typeGroup);

    // 2. SIZE group (hidden until type selected)
    const sizeGroup = mk('div', 'cp-group cp-hidden');
    sizeGroup.appendChild(mkLabel(sizeLabelText, 'cp-size-val-' + sectionId));
    const sizeRow = mk('div', 'cp-size-row');
    sizeRow.id = 'cp-sizes-' + sectionId;
    sizeGroup.appendChild(sizeRow);
    containerEl.appendChild(sizeGroup);

    // 3. COLOR group (hidden until size selected) — FINAL STEP
    const colorGroup = mk('div', 'cp-group cp-hidden');
    colorGroup.appendChild(mkLabel(colorLabelText, 'cp-color-val-' + sectionId));
    const swatchRow = mk('div', 'cp-swatch-row');
    swatchRow.id = 'cp-swatches-' + sectionId;
    colorGroup.appendChild(swatchRow);
    containerEl.appendChild(colorGroup);

    // ── Type click ────────────────────────────────────────────────────────
    typeRow.addEventListener('click', e => {
      const btn = e.target.closest('.cp-type-btn');
      if (!btn) return;
      selType = btn.dataset.value;
      selSizeRaw = null; selColor = null;

      typeRow.querySelectorAll('.cp-type-btn').forEach(b =>
        b.classList.toggle('cp-active', b.dataset.value === selType)
      );
      clearVal('cp-size-val-' + sectionId);
      clearVal('cp-color-val-' + sectionId);

      // Build size buttons for this type
      const sr = document.getElementById('cp-sizes-' + sectionId);
      sr.innerHTML = '';
      const allSizeRaws = Object.keys(vmap[selType] || {});
      // Sort by stripped display name using SIZE_ORDER
      allSizeRaws.sort((a, b) => {
        const da = stripSizePrefix(a), db = stripSizePrefix(b);
        const ai = SIZE_ORDER.indexOf(da), bi = SIZE_ORDER.indexOf(db);
        if (ai === -1 && bi === -1) return da.localeCompare(db);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      });

      allSizeRaws.forEach(raw => {
        const display = stripSizePrefix(raw);
        const btn2 = mk('button', 'cp-size-btn');
        btn2.type = 'button'; btn2.textContent = display; btn2.dataset.raw = raw;
        sr.appendChild(btn2);
      });

      sizeGroup.classList.remove('cp-hidden');
      colorGroup.classList.add('cp-hidden');
      swatchRow.innerHTML = ''; const allColors = []; Object.values(vmap[selType] || {}).forEach(set => set.forEach(c => { if (!allColors.includes(c)) allColors.push(c); })); allColors.forEach(color => { const hex = getHex(color); const light = isLight(hex); const s = mk('button', 'cp-swatch'); s.type = 'button'; s.dataset.value = color; s.style.setProperty('--sw-bg', hex); s.style.setProperty('--sw-ring', light ? '#999' : hex); s.title = color; s.setAttribute('aria-label', color); s.innerHTML = '<span class="cp-swatch-tip">' + esc(color) + '</span>'; swatchRow.appendChild(s); }); if (allColors.length) colorGroup.classList.remove('cp-hidden'); 
    });

    // ── Size click ────────────────────────────────────────────────────────
    sizeRow.addEventListener('click', e => {
      const btn = e.target.closest('.cp-size-btn');
      if (!btn || !selType) return;
      selSizeRaw = btn.dataset.raw;
      const prevColor = selColor; selColor = null; 

      sizeRow.querySelectorAll('.cp-size-btn').forEach(b =>
        b.classList.toggle('cp-active', b.dataset.raw === selSizeRaw)
      );
      setVal('cp-size-val-' + sectionId, stripSizePrefix(selSizeRaw));
      clearVal('cp-color-val-' + sectionId);

      // Build color swatches for this type + size
      const sw = document.getElementById('cp-swatches-' + sectionId);
      sw.innerHTML = '';
      const colors = Array.from(vmap[selType][selSizeRaw] || []);
      colors.forEach(color => {
        const hex = getHex(color);
        const light = isLight(hex);
        const s = mk('button', 'cp-swatch');
        s.type = 'button'; s.dataset.value = color;
        s.style.setProperty('--sw-bg', hex);
        s.style.setProperty('--sw-ring', light ? '#999' : hex);
        s.title = color;
        s.setAttribute('aria-label', color);
        s.innerHTML = `<span class="cp-swatch-tip">${esc(color)}</span>`;
        sw.appendChild(s);
      });

      colorGroup.classList.remove('cp-hidden'); if (prevColor && colors.includes(prevColor)) { const keep = Array.from(sw.querySelectorAll('.cp-swatch')).find(x => x.dataset.value === prevColor); if (keep) keep.click(); } 
    });

    // ── Color click → COMMIT to Shopify ──────────────────────────────────
    swatchRow.addEventListener('click', e => {
      const s = e.target.closest('.cp-swatch');
      if (!s || !selType) return; if (!selSizeRaw) { selColor = s.dataset.value; swatchRow.querySelectorAll('.cp-swatch').forEach(x => x.classList.toggle('cp-active', x.dataset.value === selColor)); setVal('cp-color-val-' + sectionId, selColor); return; } 
      selColor = s.dataset.value;

      swatchRow.querySelectorAll('.cp-swatch').forEach(sw =>
        sw.classList.toggle('cp-active', sw.dataset.value === selColor)
      );
      setVal('cp-color-val-' + sectionId, selColor);

      // Find native radios
      const typeInput  = findRadio(typeFS,  selType);
      const colorInput = findRadio(colorFS, selColor);
      const sizeInput  = findRadio(sizeFS,  selSizeRaw);
      if (!typeInput || !colorInput || !sizeInput) return;

      // Silently set all 3 radios (no events fired)
      uncheckAll(typeFS); uncheckAll(colorFS); uncheckAll(sizeFS);
      typeInput.checked = true; colorInput.checked = true; sizeInput.checked = true;

      // Fire ONE change event on color input → VariantSelects reads all 3 → correct selectedOptionValues
      colorInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
    if (Object.keys(vmap).length === 1) { const onlyType = typeRow.querySelector('.cp-type-btn'); if (onlyType) onlyType.click(); } 
    // ── DOM helpers ───────────────────────────────────────────────────────
    function mk(tag, cls) {
      const el = document.createElement(tag);
      if (cls) el.className = cls;
      return el;
    }
    function mkLabel(text, valId) {
      const d = mk('div', 'cp-label');
      d.innerHTML = `${esc(text)}: <span class="cp-selected-val" id="${esc(valId)}"></span>`;
      return d;
    }
    function setVal(id, v)  { const el = document.getElementById(id); if (el) el.textContent = v; }
    function clearVal(id)   { setVal(id, ''); }
    function findRadio(fs, value) {
      return fs.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
    }
    function uncheckAll(fs) {
      fs.querySelectorAll('input[type="radio"]').forEach(r => { r.checked = false; });
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  async function boot() {
    for (const el of document.querySelectorAll('.cascade-picker')) { try { const j = document.getElementById('svs-variants-' + el.dataset.section); if (j && !el.dataset.fullLoaded) { el.dataset.fullLoaded = '1'; const a = JSON.parse(j.textContent || '[]'); if (a.length >= 250) { let all = [], p = 1, pages = 1; do { const r = await fetch(location.pathname + '?view=allvariants&page=' + p); const d = await r.json(); pages = d.pages; all = all.concat(d.v); p++; } while (p <= pages); if (all.length > a.length) j.textContent = JSON.stringify(all); } } } catch (e) {} initPicker(el); try { const sec = el.closest('section') || document; const chk = () => { const needS = !!el.querySelector('.cp-size-btn'); const needC = !!el.querySelector('.cp-swatch'); const ok = (!needS || el.querySelector('.cp-size-btn.cp-active')) && (!needC || el.querySelector('.cp-swatch.cp-active')); sec.querySelectorAll('.product-form__submit, form[action*="/cart/add"] [type=submit]').forEach(b => { b.disabled = !ok; b.style.opacity = ok ? '' : '0.5'; }); sec.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout, .product-form__buttons more-payment-options-link').forEach(p => { p.style.display = ok ? '' : 'none'; }); }; el.addEventListener('click', () => setTimeout(chk, 60)); setTimeout(chk, 60); setTimeout(chk, 1500); } catch (e) {} }  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

})();
